import { useCallback, useEffect, useRef, useState } from 'react';

import {
  putBatches,
  dropped,
  jsonOf,
  mergeRemote,
  takeOnFirstContact,
  unsent,
  type Ledger,
  type RemoteMeta,
} from './shared-sync';
import type { ValidateRun } from './validate-runs';
import { parseJudge, type ValidateJudge } from './validate-judges';
import { parseSchedule, type SuiteSchedule } from './validate-schedules';
import type { SuiteRun, ValidateSuite } from './validate-suites';
import { bobcatApiUrl } from '../../utils/bobcat-api';

/**
 * Validate's history, shared: runs, suites, suite runs and judges go up to the
 * solution's store (`/ai/validate/store/*`, `bobcat/src/server-only/validate-store`) and
 * everyone's come down, so a team — and a schedule — share one list. The
 * browser copy stays as the fast, offline one; this keeps it in step.
 *
 * Where the store isn't set up (a deployment without a database) it says so
 * and the page keeps working from the browser, as before.
 */

export type SharedStatus = 'loading' | 'on' | 'unavailable' | 'error';

type Collection = 'runs' | 'suites' | 'suiteRuns' | 'schedules' | 'judges';
type Synced = 'runs' | 'suites' | 'suiteRuns' | 'judges';
/** Collections whose deletions go up too; runs and suite runs are only added. */
type Deletable = 'suites' | 'judges';

const emptyLedgers = (): Record<Synced, Ledger> => ({
  runs: new Map(),
  suites: new Map(),
  suiteRuns: new Map(),
  judges: new Map(),
});
const emptyKnown = (): Record<Deletable, Set<string>> => ({
  suites: new Set(),
  judges: new Set(),
});

/**
 * A judge the page has never synced, against the team's copy: the team's
 * wins unless the page's is a later version. Every solution starts with the
 * same two judges, so a browser opening it the first time has defaults the
 * team may long since have edited.
 */
const takeTeamJudge = (mine: ValidateJudge, theirs: ValidateJudge) =>
  theirs.version >= mine.version;

interface ListResponse {
  runs: (ValidateRun & RemoteMeta)[];
  suites: (ValidateSuite & RemoteMeta)[];
  suiteRuns: (SuiteRun & RemoteMeta)[];
  schedules: (SuiteSchedule & RemoteMeta)[];
  /** Absent from a server that predates shared judges. */
  judges?: (ValidateJudge & RemoteMeta)[];
  me: string;
}

class StoreError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

const call = async <T>(path: string, body: unknown): Promise<T> => {
  const res = await fetch(bobcatApiUrl(`/ai/validate/store/${path}`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  const json = (await res.json().catch(() => null)) as
    | (T & { error?: string; detail?: string })
    | null;
  if (!res.ok) {
    throw new StoreError(
      json?.detail ?? json?.error ?? res.statusText,
      res.status
    );
  }
  return json as T;
};

const POLL_MS = 60_000;
const PUSH_DELAY_MS = 1_500;
/** Only the newest runs go up on a sync; older ones already did, or never will matter. */
const PUSH_RUNS = 300;

export const useSharedHistory = ({
  projectId,
  solutionKey,
  runs,
  setRuns,
  suites,
  setSuites,
  suiteRuns,
  setSuiteRuns,
  judges,
  setJudges,
}: {
  projectId: string | undefined;
  solutionKey: string | null;
  runs: ValidateRun[];
  setRuns: (fn: (prev: ValidateRun[]) => ValidateRun[]) => void;
  suites: ValidateSuite[];
  setSuites: (fn: (prev: ValidateSuite[]) => ValidateSuite[]) => void;
  suiteRuns: SuiteRun[];
  setSuiteRuns: (fn: (prev: SuiteRun[]) => SuiteRun[]) => void;
  judges: ValidateJudge[];
  setJudges: (fn: (prev: ValidateJudge[]) => ValidateJudge[]) => void;
}) => {
  const [status, setStatus] = useState<SharedStatus>('loading');
  const [problem, setProblem] = useState<string | null>(null);
  const [me, setMe] = useState<string | null>(null);
  const [schedules, setSchedules] = useState<SuiteSchedule[]>([]);
  const [authors, setAuthors] = useState<Map<string, string>>(new Map());
  const ledgers = useRef<Record<Synced, Ledger>>(emptyLedgers());
  const known = useRef<Record<Deletable, Set<string>>>(emptyKnown());
  // The page's judges as of this render, for deciding first contact.
  const latestJudges = useRef(judges);
  latestJudges.current = judges;
  const scope = projectId && solutionKey ? { projectId, solutionKey } : null;
  const scopeKey = scope ? `${projectId}|${solutionKey}` : '';

  const load = useCallback(async () => {
    if (!scope) return;
    try {
      const data = await call<ListResponse>('list', scope);
      setMe(data.me);
      const by = new Map<string, string>();
      const take = <T extends { id: string }>(
        set: (fn: (prev: T[]) => T[]) => void,
        remote: (T & RemoteMeta)[],
        sync: { ledger: Ledger; takeRemote?: Set<string> }
      ) =>
        set((prev) => {
          const merged = mergeRemote(prev, remote, sync);
          merged.by.forEach((v, k) => by.set(k, v));
          return merged.list;
        });
      take(setRuns, data.runs, { ledger: ledgers.current.runs });
      take(setSuites, data.suites, { ledger: ledgers.current.suites });
      take(setSuiteRuns, data.suiteRuns, {
        ledger: ledgers.current.suiteRuns,
      });
      const teamJudges: (ValidateJudge & RemoteMeta)[] = [];
      (data.judges ?? []).forEach((raw) => {
        const judge = parseJudge(raw);
        if (judge) teamJudges.push({ ...judge, _by: raw._by ?? null });
      });
      take(setJudges, teamJudges, {
        ledger: ledgers.current.judges,
        takeRemote: takeOnFirstContact(latestJudges.current, teamJudges, {
          ledger: ledgers.current.judges,
          prefer: takeTeamJudge,
        }),
      });
      known.current = {
        suites: new Set(data.suites.map((s) => s.id)),
        judges: new Set(teamJudges.map((j) => j.id)),
      };
      // Runs list newest first, whoever made them.
      setRuns((prev) => [...prev].sort((a, b) => b.startedAt - a.startedAt));
      setSuiteRuns((prev) =>
        [...prev].sort((a, b) => b.startedAt - a.startedAt)
      );
      setAuthors(by);
      setSchedules(
        data.schedules
          .map(({ _by: _b, _updatedAt: _u, ...s }) => parseSchedule(s))
          .filter((s): s is SuiteSchedule => s !== null)
      );
      setStatus('on');
      setProblem(null);
    } catch (err) {
      if (err instanceof StoreError && err.status === 503) {
        setStatus('unavailable');
      } else {
        setStatus('error');
      }
      setProblem(err instanceof Error ? err.message : String(err));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey]);

  // Load, then keep looking for what others (and schedules) add.
  useEffect(() => {
    setStatus('loading');
    ledgers.current = emptyLedgers();
    known.current = emptyKnown();
    if (!scope) return undefined;
    void load();
    const timer = setInterval(() => void load(), POLL_MS);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey]);

  const push = useCallback(
    async <T extends { id: string }>(collection: Synced, items: T[]) => {
      if (!scope || !items.length) return;
      const ledger = ledgers.current[collection];
      // eslint-disable-next-line no-restricted-syntax
      for (const batch of putBatches(items)) {
        // eslint-disable-next-line no-await-in-loop
        await call('put', {
          ...scope,
          collection,
          records: batch.map((item) => ({
            id: item.id,
            data: item,
            updatedAt: Date.now(),
          })),
        });
        batch.forEach((item) => ledger.set(item.id, jsonOf(item)));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scopeKey]
  );

  /** Ids the store has that the page dropped: gone for everyone. */
  const sendDeletes = async (collection: Deletable, list: { id: string }[]) => {
    const gone = dropped(list, known.current[collection]);
    if (!gone.length || !scope) return;
    await call('delete', { ...scope, collection, ids: gone });
    gone.forEach((id) => {
      known.current[collection].delete(id);
      ledgers.current[collection].delete(id);
    });
  };

  // Send what changed here, a moment after it settles.
  useEffect(() => {
    if (status !== 'on') return undefined;
    const timer = setTimeout(() => {
      const { current } = ledgers;
      Promise.all([
        push('runs', unsent(runs.slice(0, PUSH_RUNS), current.runs)),
        push('suites', unsent(suites, current.suites)),
        push('suiteRuns', unsent(suiteRuns, current.suiteRuns)),
        push('judges', unsent(judges, current.judges)),
        sendDeletes('suites', suites),
        sendDeletes('judges', judges),
      ])
        .then(() => {
          suites.forEach((s) => known.current.suites.add(s.id));
          judges.forEach((j) => known.current.judges.add(j.id));
        })
        .catch((err: unknown) =>
          setProblem(err instanceof Error ? err.message : String(err))
        );
    }, PUSH_DELAY_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, runs, suites, suiteRuns, judges]);

  const saveSchedule = useCallback(
    async (schedule: SuiteSchedule) => {
      if (!scope) return;
      const res = await call<{ dueAt: Record<string, number | null> }>('put', {
        ...scope,
        collection: 'schedules' as Collection,
        records: [{ id: schedule.id, data: schedule, updatedAt: Date.now() }],
      });
      const saved = { ...schedule, nextRunAt: res.dueAt[schedule.id] ?? null };
      setSchedules((prev) => [saved, ...prev.filter((s) => s.id !== saved.id)]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scopeKey]
  );

  const deleteSchedule = useCallback(
    async (id: string) => {
      if (!scope) return;
      await call('delete', { ...scope, collection: 'schedules', ids: [id] });
      setSchedules((prev) => prev.filter((s) => s.id !== id));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scopeKey]
  );

  return {
    status,
    problem,
    me,
    /** Who made a run or suite, when someone else (or a schedule) did. */
    authorOf: (id: string): string | null => {
      const who = authors.get(id);
      return who && who !== me ? who : null;
    },
    schedules,
    saveSchedule,
    deleteSchedule,
    refresh: load,
  };
};
